import { useCallback, useEffect, useState } from "react";
import { admin } from "@v0y4ge/api-client";
import { toUINode, toUITreeNode, toUINodeKind, type UINode, type UITreeNode, type UINodeKind, type UISettingAttribution } from "./nodes-adapter";

/**
 * Node-model data hooks.
 *
 * ── REWRITTEN 2026-08-24, against wiring kit `a4aac9b0` ────────────────────
 *
 * The first cut of this file was built against an api-client that predated the
 * node-model kit — no overlay had ever been applied to this project, so
 * `admin.ts` still carried `listCompanies`/`getCompany`/`updateCompany`. Three
 * things I reported as data-layer gaps were artifacts of that one cause, and all
 * three are simply wrappers that already existed:
 *
 *   · `listNodes()` takes `{ view, page, limit }` — so the tenant-wide TREE is
 *     a request, not something to rebuild client-side from the flat list.
 *   · `getNodeResolvedSettings()` returns the cascade WITH per-key attribution,
 *     so "inherited from <ancestor>" is read rather than re-derived.
 *   · `getNodeSubtree()`, `listNodeChildren()`, `createNode()`, `reparentNode()`
 *     and `listNodeKinds()` are all typed too.
 *
 * ── WHY THESE RESPONSES ARE NOT UNWRAPPED ──────────────────────────────────
 *
 * The node functions call `apiRaw`, not `api`, and that is load-bearing. `api()`
 * strips the `data` key, which would throw away four facts the contract
 * declares: the pagination block; the DISCRIMINANT on `NodeListResponse` (flat
 * and tree are one operation with two bodies, told apart by `pagination` being
 * present or absent); the null subtree; and `warning` on the update response,
 * which rides BESIDE `data` on a 200.
 *
 * So these hooks receive envelopes and discriminate on them deliberately.
 *
 * ── WHAT IS STILL GENUINELY NOT WIRED ──────────────────────────────────────
 *
 * Per the kit, node behaviour is unchanged from `f8bb3285`: the eight vocabulary
 * WRITES still have no mock handler. Lists populate; those saves go nowhere.
 * That is an absence in the mock layer, not a missing wrapper, so the screens
 * say so at the point of action.
 */

/** Vocabulary writes with no mock handler — a save against one goes nowhere. */
export const NOT_WIRED_AT_DESIGN_TIME = [
  "createNodeKind",
  "updateNodeKind",
  "upsertNodeKindTranslation",
  "deleteNodeKindTranslation",
  "setNodeAttribute",
  "unsetNodeAttribute",
  "offerServiceAtNode",
  "withdrawServiceFromNode",
] as const;

const adm = admin as unknown as Record<string, (...args: unknown[]) => Promise<unknown>>;
const rec = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/**
 * The tenant-wide tree, as a TREE — `?view=tree`, one request.
 *
 * The reduced eight-key shape comes back nested, with no pagination envelope.
 * A flat list is fetched alongside it because the tree projection deliberately
 * omits `path` and `depth`, and the drag-legality checks (cycle, depth cap) are
 * computed from exactly those two fields.
 */
export function useNodeTree() {
  const [tree, setTree] = useState<UITreeNode[]>([]);
  const [nodes, setNodes] = useState<UINode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  /** A reparent invalidates the WHOLE tree, not one row: one UPDATE rewrites
   *  path and depth for the moved node AND every descendant. Refetch the tree. */
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    Promise.all([
      adm.listNodes({ view: "tree" }),
      adm.listNodes({ view: "flat", limit: 200 }),
    ])
      .then(([treeRaw, flatRaw]) => {
        if (!live) return;
        // Discriminate rather than assume: the tree branch carries no `pagination`.
        const t = rec(treeRaw).data;
        setTree(Array.isArray(t) ? t.map(toUITreeNode) : []);
        const f = rec(flatRaw).data;
        setNodes(Array.isArray(f) ? f.map(toUINode).filter((n) => n.id) : []);
        setError(null);
      })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [tick]);

  return { tree, nodes, loading, error, reload };
}

/**
 * The tenant's node kinds — its own level vocabulary, not a platform enum.
 *
 * These ids are what `applies_to_kinds` stores, and this endpoint is the only
 * place they are obtainable from the API.
 */
export function useNodeKinds() {
  const [kinds, setKinds] = useState<UINodeKind[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    adm.listNodeKinds({ limit: 100 })
      .then((raw) => {
        if (!live) return;
        const d = rec(raw).data;
        setKinds(Array.isArray(d) ? d.map(toUINodeKind).filter((k) => k.id) : []);
      })
      .catch(() => { /* leave empty — the screens say what an empty list means */ })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  return { kinds, loading };
}

/**
 * One node's resolved settings PLUS per-key attribution.
 *
 * The attribution map is the whole point: for every resolved key it names the
 * ancestor the value actually came from, so the UI reads "inherited from X"
 * off the response instead of re-deriving the cascade and asserting its own
 * answer — and "this node sets it" stays distinguishable from "this node
 * happens to agree with its parent".
 */
export function useResolvedSettings(nodeId: string | null) {
  const [resolved, setResolved] = useState<Record<string, unknown>>({});
  const [attribution, setAttribution] = useState<Record<string, UISettingAttribution>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!nodeId) { setResolved({}); setAttribution({}); return; }
    let live = true;
    setLoading(true);
    adm.getNodeResolvedSettings(nodeId)
      .then((raw) => {
        if (!live) return;
        const d = rec(rec(raw).data);
        setResolved(rec(d.resolved_settings));
        setAttribution(rec(d.attribution) as Record<string, UISettingAttribution>);
      })
      .catch(() => { if (live) { setResolved({}); setAttribution({}); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [nodeId]);

  return { resolved, attribution, loading };
}
